import { useState, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { App } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import type { StakeholderTemplate, FeedbackStyle } from '@bandos/shared'
import type { FormData, RightPanelView, ArrayField } from '../CharacterSelectionModal.types'
import { emptyFormData } from '../CharacterSelectionModal.types'

interface UseCharacterFormParams {
  createTemplate: (
    data: StakeholderTemplate
  ) => Promise<{ success: boolean; error?: string; errorCode?: string }>
  updateTemplate: (
    id: string,
    data: Partial<StakeholderTemplate>
  ) => Promise<{ success: boolean; error?: string; template?: StakeholderTemplate }>
  deleteTemplate: (id: string) => Promise<{ success: boolean; error?: string }>
  loadTemplates: () => Promise<void>
  templates: StakeholderTemplate[]
  markGeneratingPortrait: (id: string) => void
  selectedTemplate: StakeholderTemplate | null
  setSelectedTemplate: (t: StakeholderTemplate | null) => void
  setRightPanelView: (view: RightPanelView) => void
}

export function useCharacterForm({
  createTemplate,
  updateTemplate,
  deleteTemplate,
  loadTemplates,
  templates,
  markGeneratingPortrait,
  selectedTemplate,
  setSelectedTemplate,
  setRightPanelView,
}: UseCharacterFormParams) {
  const { message, modal } = App.useApp()

  const [formData, setFormData] = useState<FormData>({ ...emptyFormData })
  const [editingTemplate, setEditingTemplate] = useState<StakeholderTemplate | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const resetForm = useCallback(() => {
    setFormData({ ...emptyFormData })
    setEditingTemplate(null)
  }, [])

  const populateFormFromTemplate = useCallback((template: StakeholderTemplate) => {
    setFormData({
      name: template.name || '',
      role: template.role ?? '',
      role_summary: template.role_summary ?? '',
      goals: template.goals?.length ? [...template.goals] : [''],
      concerns: template.concerns?.length ? [...template.concerns] : [''],
      success_metrics: template.success_metrics?.length ? [...template.success_metrics] : [''],
      extra_rules: template.feedback_style?.extra_rules?.length
        ? [...template.feedback_style.extra_rules]
        : [''],
    })
  }, [])

  const updateFormField = useCallback((field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const addArrayItem = useCallback((field: ArrayField) => {
    setFormData((prev) => ({ ...prev, [field]: [...prev[field], ''] }))
  }, [])

  const removeArrayItem = useCallback((field: ArrayField, index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }))
  }, [])

  const updateArrayItem = useCallback((field: ArrayField, index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }))
  }, [])

  const buildTemplateData = useCallback(() => {
    const feedbackStyle: FeedbackStyle = {
      extra_rules: formData.extra_rules.filter((r) => r.trim()),
    }
    return {
      name: formData.name.trim(),
      role: formData.role.trim() || undefined,
      role_summary: formData.role_summary.trim() || undefined,
      goals: formData.goals.filter((g) => g.trim()),
      concerns: formData.concerns.filter((c) => c.trim()),
      success_metrics: formData.success_metrics.filter((m) => m.trim()),
      feedback_style: feedbackStyle,
    }
  }, [formData])

  const handleCreateNewClick = useCallback(() => {
    setSelectedTemplate(null)
    setEditingTemplate(null)
    setRightPanelView('create')
    resetForm()
  }, [resetForm, setSelectedTemplate, setRightPanelView])

  const handleFormCancel = useCallback(() => {
    setRightPanelView('empty')
    resetForm()
  }, [resetForm, setRightPanelView])

  const handleCreateSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      message.error('Name is required')
      return
    }

    try {
      setSubmitting(true)
      const templateId = uuidv4().toLowerCase()
      const templateData: StakeholderTemplate = {
        ...buildTemplateData(),
        id: templateId,
        is_public: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }

      const result = await createTemplate(templateData)

      if (result.success) {
        message.success('Team member created')
        markGeneratingPortrait(templateId)
        await loadTemplates()

        const newTemplate = templates.find((t) => t.id === templateId) ?? templateData
        setSelectedTemplate(newTemplate)
        setRightPanelView('profile')
        resetForm()
      } else {
        message.error(result.error ?? 'Failed to create')
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.stakeholder', action: 'create_template' },
      })
      message.error('Failed to create team member')
    } finally {
      setSubmitting(false)
    }
  }, [
    formData,
    buildTemplateData,
    createTemplate,
    message,
    templates,
    loadTemplates,
    resetForm,
    markGeneratingPortrait,
    setSelectedTemplate,
    setRightPanelView,
  ])

  const handleEditClick = useCallback(
    (template: StakeholderTemplate, e: React.MouseEvent) => {
      e.stopPropagation()
      setSelectedTemplate(template)
      setEditingTemplate(template)
      populateFormFromTemplate(template)
      setRightPanelView('edit')
    },
    [populateFormFromTemplate, setSelectedTemplate, setRightPanelView]
  )

  const handleEditSave = useCallback(async () => {
    if (!editingTemplate || editingTemplate.is_public) return
    if (!formData.name.trim()) {
      message.error('Name is required')
      return
    }

    try {
      setSubmitting(true)
      const result = await updateTemplate(editingTemplate.id, buildTemplateData())

      if (result.success) {
        message.success('Changes saved')
        await loadTemplates()

        const updatedTemplate = result.template ?? { ...editingTemplate, ...buildTemplateData() }
        setSelectedTemplate(updatedTemplate)
        setRightPanelView('profile')
        resetForm()
      } else {
        message.error(result.error ?? 'Failed to save')
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.stakeholder', action: 'edit_template' },
      })
      message.error('Failed to save changes')
    } finally {
      setSubmitting(false)
    }
  }, [
    editingTemplate,
    formData,
    buildTemplateData,
    updateTemplate,
    message,
    loadTemplates,
    resetForm,
    setSelectedTemplate,
    setRightPanelView,
  ])

  const handleSaveAsNew = useCallback(async () => {
    if (!formData.name.trim()) {
      message.error('Name is required')
      return
    }

    try {
      setSubmitting(true)
      const templateId = uuidv4().toLowerCase()
      const templateData: StakeholderTemplate = {
        ...buildTemplateData(),
        id: templateId,
        is_public: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }

      const result = await createTemplate(templateData)

      if (result.success) {
        message.success('Saved as new team member')
        markGeneratingPortrait(templateId)
        await loadTemplates()

        const newTemplate = templates.find((t) => t.id === templateId) ?? templateData
        setSelectedTemplate(newTemplate)
        setRightPanelView('profile')
        resetForm()
      } else {
        message.error(result.error ?? 'Failed to save')
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.stakeholder', action: 'save_as_new_template' },
      })
      message.error('Failed to save as new')
    } finally {
      setSubmitting(false)
    }
  }, [
    formData,
    buildTemplateData,
    createTemplate,
    message,
    templates,
    loadTemplates,
    resetForm,
    markGeneratingPortrait,
    setSelectedTemplate,
    setRightPanelView,
  ])

  const handleDeleteTemplate = useCallback(() => {
    if (!selectedTemplate || selectedTemplate.is_public) return

    modal.confirm({
      title: 'Delete team member',
      icon: null,
      content: `Are you sure you want to delete "${selectedTemplate.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setSubmitting(true)
          const result = await deleteTemplate(selectedTemplate.id)

          if (result.success) {
            message.success(`${selectedTemplate.name} deleted`)
            await loadTemplates()
            setSelectedTemplate(null)
            setRightPanelView('empty')
          } else {
            message.error(result.error ?? 'Failed to delete')
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: 'mindmap.stakeholder', action: 'delete_template' },
          })
          message.error('Failed to delete team member')
        } finally {
          setSubmitting(false)
        }
      },
    })
  }, [
    selectedTemplate,
    deleteTemplate,
    message,
    modal,
    loadTemplates,
    setSelectedTemplate,
    setRightPanelView,
  ])

  return {
    formData,
    editingTemplate,
    submitting,
    resetForm,
    updateFormField,
    addArrayItem,
    removeArrayItem,
    updateArrayItem,
    handleCreateNewClick,
    handleFormCancel,
    handleCreateSubmit,
    handleEditClick,
    handleEditSave,
    handleSaveAsNew,
    handleDeleteTemplate,
  }
}
